/**
 * QuizPreview — drive a quiz through the presentation view without
 * creating a live event. No socket, no PINs, no audience involvement.
 *
 * The host can step through every slide they'd see during a real night
 * (welcome, round intros, questions, song slides, visual rounds, round
 * summaries with answers revealed, standings stub) to sanity-check their
 * quiz before they commit to launching it.
 *
 * Reuses the PresentationView CSS so the visual language matches the
 * audience experience exactly.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Button, Flex, Text } from '@radix-ui/themes';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../../utils/api';
import { useOutroLinks } from '../Presentation/PresentationView';
import '../Presentation/PresentationView.css';

type QuestionType = 'TEXT' | 'SONG' | 'IMAGE';
type RoundType = 'STANDARD' | 'VISUAL' | 'AUDIO';

interface PreviewQuestion {
  id: string;
  orderIndex: number;
  type: QuestionType;
  text: string;
  answer: string;
  answerPartB: string | null;
  songTitle: string | null;
  songArtist: string | null;
  audioPath: string | null;
  imagePath: string | null;
}

interface PreviewRound {
  id: string;
  orderIndex: number;
  title: string;
  roundType: RoundType;
  twoAnswerMode: boolean;
  questions: PreviewQuestion[];
}

interface PreviewQuiz {
  id: string;
  name: string;
  welcomePrizes: string | null;
  welcomeStartTime: string | null;
  rulesText: string | null;
  showDrawingSlide: boolean;
  drawingAfterRound: number | null;
  showOutroSlide: boolean;
  standingsHighlightCount: number;
  rounds: PreviewRound[];
}

type SlideKind =
  | 'WELCOME'
  | 'RULES'
  | 'ROUNDS_OVERVIEW'
  | 'ROUND_INTRO'
  | 'QUESTION'
  | 'ANSWER'
  | 'SONG_BONUS'
  | 'VISUAL_ROUND'
  | 'ROUND_SUMMARY'
  | 'STANDINGS'
  | 'DRAWING'
  | 'OUTRO';

interface SlideEntry {
  kind: SlideKind;
  roundIdx: number;
  questionIdx: number;
}

// Mirror of HostPanel.buildSlideSequence so a preview walks the exact
// same path a real host would walk on game night.
function buildSequence(quiz: PreviewQuiz): SlideEntry[] {
  const { rounds } = quiz;
  const seq: SlideEntry[] = [];
  seq.push({ kind: 'WELCOME', roundIdx: 0, questionIdx: 0 });
  if (quiz.rulesText && quiz.rulesText.trim()) {
    seq.push({ kind: 'RULES', roundIdx: 0, questionIdx: 0 });
  }
  if (rounds.length > 0) {
    seq.push({ kind: 'ROUNDS_OVERVIEW', roundIdx: 0, questionIdx: 0 });
  }
  for (let ri = 0; ri < rounds.length; ri++) {
    const round = rounds[ri]!;
    seq.push({ kind: 'ROUND_INTRO', roundIdx: ri, questionIdx: 0 });
    if (round.roundType === 'VISUAL') {
      seq.push({ kind: 'VISUAL_ROUND', roundIdx: ri, questionIdx: 0 });
    } else {
      for (let qi = 0; qi < round.questions.length; qi++) {
        const q = round.questions[qi]!;
        const kind: SlideKind =
          q.type === 'SONG' ? 'SONG_BONUS' :
          q.type === 'IMAGE' ? 'VISUAL_ROUND' :
                               'QUESTION';
        seq.push({ kind, roundIdx: ri, questionIdx: qi });
      }
    }
    if (round.questions.length > 0 && round.roundType !== 'AUDIO') {
      seq.push({ kind: 'ROUND_SUMMARY', roundIdx: ri, questionIdx: 0 });
    }
    // Drawing slot — match HostPanel's logic so /preview walks the
    // same sequence as a live event.
    const drawingHere = (() => {
      if (quiz.showDrawingSlide === false) return false;
      if (quiz.drawingAfterRound != null && quiz.drawingAfterRound > 0) {
        return ri === quiz.drawingAfterRound - 1;
      }
      return ri === rounds.length - 1;
    })();
    if (drawingHere) {
      seq.push({ kind: 'DRAWING', roundIdx: ri, questionIdx: 0 });
    }
    seq.push({ kind: 'STANDINGS', roundIdx: ri, questionIdx: 0 });
  }
  if (rounds.length > 0 && quiz.showOutroSlide !== false) {
    seq.push({ kind: 'OUTRO', roundIdx: rounds.length - 1, questionIdx: 0 });
  }
  return seq;
}

const QuizPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const outroLinks = useOutroLinks();
  const [quiz, setQuiz] = useState<PreviewQuiz | null>(null);
  const [pos, setPos] = useState(0);
  // In preview mode the host can flip answers/summary visibility on the
  // fly without a PIN — there's no audience to protect.
  const [answerShown, setAnswerShown] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/quizzes/${id}`).then((res) => {
      setQuiz(res.data?.data?.quiz ?? null);
    });
  }, [id]);

  const seq = quiz ? buildSequence(quiz) : [];
  const slide = seq[pos];
  const round = slide && quiz ? quiz.rounds[slide.roundIdx] : null;
  const question = slide && round ? round.questions[slide.questionIdx] : null;

  const next = useCallback(() => {
    setPos((p) => Math.min(p + 1, seq.length - 1));
    setAnswerShown(false);
  }, [seq.length]);

  const prev = useCallback(() => {
    setPos((p) => Math.max(p - 1, 0));
    setAnswerShown(false);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key.toLowerCase() === 'a') setAnswerShown((s) => !s);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  if (!quiz) {
    return (
      <Flex align="center" justify="center" minHeight="60vh">
        <Text size="3" color="gray">Loading quiz preview…</Text>
      </Flex>
    );
  }
  if (!slide) {
    return (
      <Flex align="center" justify="center" minHeight="60vh">
        <Text size="3" color="gray">Quiz has no slides to preview.</Text>
      </Flex>
    );
  }

  return (
    <Box className="presentation-view" style={{ minHeight: 'calc(100vh - 80px)' }}>
      {/* Preview badge — makes it impossible to mistake this for a real
          live event. Stays visible the whole time. */}
      <div
        style={{
          position: 'fixed',
          top: '0.75rem',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(245, 166, 35, 0.95)',
          color: '#0f0f0f',
          fontWeight: 700,
          fontSize: '0.85rem',
          padding: '0.35rem 0.85rem',
          borderRadius: 999,
          letterSpacing: '0.1em',
          zIndex: 9999,
        }}
      >
        PREVIEW · NOT LIVE
      </div>

      {slide.kind === 'WELCOME' && (
        <div className="slide slide-welcome">
          <div className="welcome-content">
            <h1 className="welcome-title">{quiz.name}</h1>
            {(quiz.welcomeStartTime || quiz.welcomePrizes) && (
              <div className="welcome-meta">
                {quiz.welcomeStartTime && (
                  <div className="welcome-meta-row">
                    <span className="welcome-meta-label">Starts</span>
                    <span className="welcome-meta-value">{quiz.welcomeStartTime}</span>
                  </div>
                )}
                {quiz.welcomePrizes && (
                  <div className="welcome-meta-row">
                    <span className="welcome-meta-label">Prizes</span>
                    <ul className="welcome-prizes-list">
                      {quiz.welcomePrizes
                        .split(/[\r\n]+|\s+·\s+|\s+\|\s+/)
                        .map((line) => line.trim())
                        .filter(Boolean)
                        .map((line, i) => (
                          <li key={i} className="welcome-prizes-item">{line}</li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {slide.kind === 'RULES' && (
        <div className="slide slide-rules">
          <div className="rules-content">
            <span className="rules-overline">Tonight&apos;s</span>
            <h1 className="rules-heading">Rules</h1>
            <ul className="rules-list">
              {(quiz.rulesText || '')
                .split(/\r?\n/)
                .map((l) => l.trim())
                .filter(Boolean)
                .map((line, i) => <li key={i} className="rules-item">{line}</li>)}
            </ul>
          </div>
        </div>
      )}

      {slide.kind === 'ROUNDS_OVERVIEW' && (
        <div className="slide slide-rounds-overview">
          <div className="overview-content">
            <span className="overview-overline">Tonight&apos;s Rounds</span>
            <h1 className="overview-heading">{quiz.rounds.length} rounds</h1>
            <ol className="overview-list">
              {quiz.rounds.map((r, i) => (
                <li key={r.id} className="overview-item">
                  <span className="overview-num">{i + 1}</span>
                  <span className="overview-title">{r.title || 'Untitled round'}</span>
                  {r.roundType !== 'STANDARD' && (
                    <span className={`overview-type ${r.roundType.toLowerCase()}`}>
                      {r.roundType}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {slide.kind === 'ROUND_INTRO' && round && (
        <div className="slide slide-round-intro">
          <div className="round-intro-content">
            <span className="round-overline">Round</span>
            <h1 className="round-number">{round.orderIndex + 1}</h1>
            <h2 className="round-title-text">{round.title}</h2>
            {round.roundType !== 'STANDARD' && (
              <span className={`round-type-pill ${round.roundType.toLowerCase()}`}>
                {round.roundType} ROUND
              </span>
            )}
          </div>
        </div>
      )}

      {slide.kind === 'QUESTION' && question && (
        <div className="slide slide-question">
          <div className="question-header">
            <span className="q-round-label">Round {round!.orderIndex + 1}</span>
            <span className="q-number-label">Question {question.orderIndex + 1}</span>
          </div>
          <div className="question-body">
            <h1 className="question-text">{question.text}</h1>
            {answerShown && (
              <Box mt="6" style={{ textAlign: 'center' }}>
                <Text size="2" color="gray">Answer</Text>
                <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: 4 }}>
                  {question.answer}
                  {round!.twoAnswerMode && question.answerPartB && ` / ${question.answerPartB}`}
                </div>
              </Box>
            )}
          </div>
        </div>
      )}

      {slide.kind === 'SONG_BONUS' && question && (
        <div className="slide slide-song">
          <div className="song-content">
            <span className="song-overline">Song Question</span>
            <h1 className="song-heading">{question.text || "What's the song and artist?"}</h1>
            {question.audioPath && (
              <audio ref={audioRef} controls className="song-player">
                <source src={question.audioPath} />
              </audio>
            )}
            {answerShown && (
              <Box mt="4" style={{ textAlign: 'center' }}>
                <Text size="2" color="gray">Answer</Text>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 4 }}>
                  {[question.songTitle, question.songArtist].filter(Boolean).join(' — ') || question.answer}
                </div>
              </Box>
            )}
          </div>
        </div>
      )}

      {slide.kind === 'VISUAL_ROUND' && round && (
        <div className="slide slide-visual">
          <div className="visual-content">
            <span className="visual-overline">Visual Round</span>
            <h1 className="visual-heading">{round.title || 'Visual Round'}</h1>
            <p className="visual-desc">
              {question
                ? question.text || 'Visual prompt'
                : 'Grab a visual round sheet from your host'}
            </p>
            {question?.imagePath && (
              <img
                className="visual-image"
                src={question.imagePath}
                alt={answerShown ? question.answer || 'Visual round image' : 'Visual round image'}
                style={{ maxWidth: '70%', maxHeight: '55vh', objectFit: 'contain', borderRadius: 8 }}
              />
            )}
            {answerShown && question && question.answer && (
              <Box mt="4" style={{ textAlign: 'center' }}>
                <Text size="2" color="gray">Answer</Text>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 4 }}>
                  {question.answer}
                </div>
              </Box>
            )}
          </div>
        </div>
      )}

      {slide.kind === 'ROUND_SUMMARY' && round && (
        <div className="slide slide-summary">
          <div className="summary-content">
            <span className="summary-overline">Round {round.orderIndex + 1} — {round.title}</span>
            <h1 className="summary-heading">{answerShown ? 'Answers' : 'Round Summary'}</h1>
            <ol className="summary-questions">
              {round.questions.map((q, i) => (
                <li key={i} className="summary-question-item">
                  <span>{q.text}</span>
                  {answerShown && q.answer && (
                    <span>
                      {' '}— <strong>{q.answer}</strong>
                      {q.answerPartB && <em> / {q.answerPartB}</em>}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {slide.kind === 'STANDINGS' && (
        <div className="slide slide-standings">
          <div className="standings-heading-row">
            <h1 className="standings-heading">Standings</h1>
          </div>
          <Box style={{ textAlign: 'center', opacity: 0.55 }}>
            <Text size="3">No team scores in preview mode.</Text>
          </Box>
        </div>
      )}

      {slide.kind === 'OUTRO' && (
        <div className="slide slide-outro">
          <div className="outro-content">
            <div className="outro-header">
              <span className="outro-overline">Thanks for playing</span>
            </div>
            <div className="outro-schedule">
              <h2 className="outro-section-heading">Next up</h2>
              <p className="outro-empty">Live schedule shows here during a real event. Themed nights ("80s Music", "Halloween") appear under each event.</p>
            </div>
            <div className="outro-qrs">
              {outroLinks.map((link) => (
                <div key={link.url} className="outro-qr">
                  <div className="outro-qr-frame">
                    <QRCodeSVG value={link.url} size={256} bgColor="#ffffff" fgColor="#0a0a1a" level="M" marginSize={1} />
                  </div>
                  <span className="outro-qr-handle">
                    <FontAwesomeIcon icon={link.icon} className="outro-qr-icon" />
                    {link.handle}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {slide.kind === 'DRAWING' && (
        <div className="slide slide-drawing">
          <div className="drawing-content">
            <span className="drawing-overline">Random Drawing</span>
            <h1 className="drawing-heading">Spin the wheel</h1>
            <p className="drawing-sub">
              In a live event the audience screen shows a Draw button here.
              Click it during the night to pick a random winner from teams
              ranked 3rd or lower (and still in the room).
            </p>
            <button
              type="button"
              className="drawing-button"
              disabled
              title="Drawing only works in a live event"
            >
              Draw Winner (preview)
            </button>
          </div>
        </div>
      )}

      {/* Preview-only navigation bar — fixed at the bottom so it never
          overlaps slide content. */}
      <Flex
        gap="3"
        align="center"
        justify="center"
        style={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.55)',
          padding: '0.5rem 1rem',
          borderRadius: 999,
          zIndex: 9999,
        }}
      >
        <Button variant="soft" onClick={prev} disabled={pos === 0}>← Prev</Button>
        <Text size="2" color="gray" style={{ minWidth: 80, textAlign: 'center' }}>
          {pos + 1} / {seq.length}
        </Text>
        <Button onClick={next} disabled={pos === seq.length - 1}>Next →</Button>
        <Button variant="ghost" onClick={() => setAnswerShown((s) => !s)}>
          {answerShown ? 'Hide answer' : 'Show answer'}
        </Button>
        <Button asChild variant="ghost">
          <Link to={`/quizzes/${quiz.id}/edit`}>Close preview</Link>
        </Button>
      </Flex>
    </Box>
  );
};

export default QuizPreview;
